import { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, CalendarDaysIcon, DocumentTextIcon, DocumentCheckIcon, ExclamationCircleIcon, ScaleIcon, ArrowsRightLeftIcon, ChevronDownIcon, XCircleIcon, ClockIcon, ShieldExclamationIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/16/solid';
import Avatar from '../../../../components/ui/Avatar/Avatar';
import CategoryTag from '../CategoryTag';
import { formatTimestamp } from '../../../../utils/discipline/formatters';
import AttachmentList from '../../../../components/attachments/AttachmentList';
import RecordActionsMenu from './RecordActionsMenu';
import ObligationAuditDetails from './ObligationAuditDetails';
import Modal from '../../../../components/ui/Modal/Modal';
import { obligationStatePresentation, requiresActionForEvent } from '../../../../utils/discipline/obligationState';
import { getBehaviourResponseExplanation } from '@/services/discipline/utils/responseExplanation';
import { getConsequenceSummary } from '@/services/discipline/utils/consequenceSummary';
import { getLinkedCorrectionRecords } from '../../../../utils/discipline/recordRelationships';

const ROSTER_ACTION_IDS = ['mark-served', 'mark-missed', 'mark-absent', 'mark-late', 'mark-excused', 'reset-status'];
const HEADER_ACTION_IDS = ROSTER_ACTION_IDS.filter(id => id !== 'reset-status');
const FOLLOW_UP_RESOLUTION_OPTIONS = [
  { id: 'attended-correction', outcome: 'attended-correction', label: 'Learner attended — not recorded' },
  { id: 'attended-late', outcome: 'attended-late', label: 'Attended late' },
  { id: 'absent-from-school', outcome: 'absent-from-school', label: 'Absent from school' },
  { id: 'did-not-complete', outcome: 'did-not-complete', label: 'Did not complete' },
];

const StatusActionButton = memo(({ action, isActive, onClick }) => (
  <button
    type="button"
    className={`btn btn-sm min-h-11 h-11 min-w-11 px-2 text-xs sm:px-3 ${
      isActive ? action.className : `btn-outline ${action.className}`
    }`}
    onClick={(clickEvent) => {
      clickEvent.stopPropagation();
      onClick();
    }}
    aria-pressed={isActive}
  >
    <span className="truncate">{action.label}</span>
  </button>
));
StatusActionButton.displayName = 'StatusActionButton';

// Map event.status to the active action button id
const STATUS_TO_ACTION_ID = {
  served: 'mark-served',
  missed: 'mark-missed',
  absent: 'mark-absent',
  late: 'mark-late',
};

// Simple time ago formatter
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
};

// Countdown formatter for queued messages
// Worker runs every 2 minutes at: 00:00, 00:02, 00:04, etc. (even minute marks)
const formatCountdown = () => {
  const now = new Date();
  const currentMinute = now.getMinutes();
  const currentSecond = now.getSeconds();

  // Calculate next even minute (0, 2, 4, 6, ...)
  const nextEvenMinute = currentMinute % 2 === 0 ? currentMinute + 2 : currentMinute + 1;

  // Calculate seconds until next run
  const minutesUntilNext = nextEvenMinute - currentMinute;
  const secondsUntilNext = (minutesUntilNext * 60) - currentSecond;

  const mins = Math.floor(secondsUntilNext / 60);
  const secs = secondsUntilNext % 60;

  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

// Countdown component that updates every second
const QueuedCountdown = () => {
  const [countdown, setCountdown] = useState(formatCountdown());

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(formatCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-1 text-base-content/50 animate-pulse">
      <span className="text-xs opacity-70">
        {countdown}
      </span>
    </div>
  );
};

// A single constituent record in a causal chain (e.g. a demerit beneath a
// detention, or a mention beneath a demerit). Drillable: when the record has
// its own constituents, a chevron expands them inline, indented, recursively.
export const TriggerRow = ({
  trigger,
  eventsWithCounts,
  eventTypeConfig,
  onNavigate,
  depth = 0,
  narrow = false,
}) => {
  const [open, setOpen] = useState(false);
  const matchingEvent = eventsWithCounts?.find(e => e.id === trigger.id);
  const triggerTypeConfig = eventTypeConfig[trigger.type] || eventTypeConfig.mention || {};
  const children = trigger.triggeredByDetails || [];
  const hasChildren = children.length > 0;

  let triggerDate;
  try {
    triggerDate = trigger.timestamp?.toDate ? trigger.timestamp.toDate() : new Date(trigger.timestamp);
  } catch (e) {
    triggerDate = new Date();
  }

  // Trim the leading "<name> received " preamble so the offence reads cleanly.
  const offenceText = (() => {
    let text = trigger.offence;
    if (text && text.includes(' received ')) {
      const parts = text.split(' received ');
      if (parts.length > 1) text = parts[1];
    }
    return text;
  })();

  return (
    <div className="min-w-0">
      <div
        data-related-event-id={trigger.id}
        className={`
          w-full rounded-lg border p-2 min-w-0
          ${triggerTypeConfig.bgColor} ${triggerTypeConfig.borderColor}
          hover:shadow-md transition-all
          flex items-center gap-2 flex-wrap
        `}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
            className="flex-shrink-0 p-0.5 rounded hover:bg-base-content/10 transition-colors"
            aria-label={open ? 'Hide what led to this' : 'Show what led to this'}
          >
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          <span className="w-3.5 flex-shrink-0" aria-hidden="true" />
        )}
        <button
          onClick={() => onNavigate(trigger.id)}
          className={`flex flex-1 min-w-0 text-left cursor-pointer ${
            narrow
              ? 'flex-col items-start gap-1'
              : 'items-center gap-2 flex-wrap'
          }`}
        >
          <span className={`badge badge-sm ${triggerTypeConfig.iconBg} ${triggerTypeConfig.iconColor} font-medium flex-shrink-0`}>
            {triggerTypeConfig.label} {matchingEvent?.sequentialCount}
          </span>
          <span className={`text-xs font-medium text-base-content/90 min-w-0 break-words ${narrow ? 'w-full' : 'flex-1'}`}>
            {offenceText}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {trigger.level && (
              <span className="badge badge-xs badge-outline">
                lvl {trigger.level}
              </span>
            )}
            <span className="text-xs text-base-content/60 whitespace-nowrap">
              {triggerDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        </button>
      </div>

      {hasChildren && open && (
        <div className="mt-1.5 ml-3 pl-2 border-l-2 border-base-300 space-y-1.5">
          {[...children].reverse().map(child => (
            <TriggerRow
              key={child.id}
              trigger={child}
              eventsWithCounts={eventsWithCounts}
              eventTypeConfig={eventTypeConfig}
              onNavigate={onNavigate}
              depth={depth + 1}
              narrow={narrow}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TimelineEventCard = ({
  event,
  eventTypeConfig,
  categories,
  eventsWithCounts,
  selectionMode,
  isSelected,
  onToggleSelection,
  onNavigate,
  onQuickViewProfile,
  availableActions = [],
  onAction,
  isFiltered = false,
  compact = false,
  fullWidth = false,
  showMessageTimestamp = true,
  messageStatusesLoaded = true,
  showActionControls = false,
  showFollowUpControls = showActionControls,
  isActionedInCurrentSession = false,
  initiallyExpanded = false,
  highlighted = false,
  anchorId,
  canManageRoster = false,
  availabilityDisplayEnabled = false,
  contextAction = null,
  showObligationState = true,
  showCompletionLabel = true,
  showAdministrationDetails = false,
  auditActorNamesById = {},
  onClick
}) => {
  // Deep-link arrivals (e.g. a bell breadcrumb to a specific detention) open the
  // card already expanded so the constituent chain is visible without a click.
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [contextConfirmationOpen, setContextConfirmationOpen] = useState(false);
  const responseExplanation = getBehaviourResponseExplanation(event);

  // The useState seed only applies at first mount. When a card is already
  // mounted and THEN becomes the deep-link target (e.g. the user clicks the
  // bell while the queue is open), the seed never re-runs. This effect opens
  // the card whenever it becomes the deep-link target, covering both the
  // fresh-mount and already-mounted cases. We never auto-collapse here.
  useEffect(() => {
    if (initiallyExpanded) setIsExpanded(true);
  }, [initiallyExpanded]);

  // Use eventTypeConfig for both mentions and consequences with better debugging
  const typeConfig = eventTypeConfig[event.type] || (() => {
    console.warn(`[TimelineEventCard] Missing config for type "${event.type}" - available types:`, Object.keys(eventTypeConfig));
    return eventTypeConfig.unknown || {};
  })();
  const consequenceSummary = getConsequenceSummary(event, typeConfig);

  const headerActions = availableActions.filter(action =>
    HEADER_ACTION_IDS.includes(action.id) && typeof action.handler === 'function'
  );
  const secondaryActions = availableActions.filter(action =>
    !ROSTER_ACTION_IDS.includes(action.id)
  );
  const activeActionId = event.attendanceStatus === 'excused'
    ? 'mark-excused'
    : STATUS_TO_ACTION_ID[event.status] || null;
  const resetAction = availableActions.find(action =>
    action.id === 'reset-status' && typeof action.handler === 'function'
  );
  const isQueueConsequence =
    event.sourceCollection === 'disciplineQueue' || event.sourceCollection === 'queue';
  const contextSubjects = [...new Set([
    event.originalData?.subject,
    ...(event.sourceSubjects || []),
  ].filter(Boolean))];
  const contextIssuedByNames = [...new Set([
    event.issuedByName,
    ...(event.sourceIssuedByNames || []),
  ].filter(Boolean))];
  const obligationPresentation = isQueueConsequence && requiresActionForEvent(event, eventTypeConfig)
    ? obligationStatePresentation(event)
    : null;
  const runHeaderAction = (action) => {
    const actionToRun = activeActionId === action.id ? resetAction : action;
    if (!actionToRun) return;
    if (onAction) {
      onAction(actionToRun);
    } else {
      actionToRun.handler();
    }
  };

  // Debug: Log when type config is missing
  if (!eventTypeConfig[event.type] && event.type !== 'duplicate-resolution') {
    console.warn(`[TimelineEventCard] No config found for type: "${event.type}"`, {
      eventId: event.id,
      studentName: event.studentName,
      availableTypes: Object.keys(eventTypeConfig).sort(),
      configIsEmpty: Object.keys(eventTypeConfig).length === 0,
      eventTypeConfig: eventTypeConfig
    });
  }

  // Attendance status (used for badge display only in TimelineEventCard)
  const attendanceMarked = event.attendanceStatus;
  const followUpStatus =
    event.followUpStatus || event.originalData?.followUpStatus;
  const hasOpenFollowUp =
    followUpStatus !== 'completed' &&
    (
      ['open', 'escalation-required', 'communication-required',
        'communication-failed'].includes(followUpStatus) ||
      ['missed', 'absent'].includes(event.status)
    );
  const showRosterHeaderActions =
    showActionControls &&
    isQueueConsequence &&
    (!hasOpenFollowUp || isActionedInCurrentSession) &&
    headerActions.length > 0;
  const showFollowUpHeaderActions =
    canManageRoster &&
    showFollowUpControls &&
    isQueueConsequence &&
    !isActionedInCurrentSession &&
    event.status !== 'rescheduled' &&
    event.status !== 'escalated' &&
    event.status !== 'served' &&
    hasOpenFollowUp;
  const operationalOverflowActions = showFollowUpHeaderActions
    ? [
        {
          id: 'schedule-next-availability',
          type: 'schedule-next-availability',
          label: 'Renew',
          description: 'Move this same consequence to the learner’s next available session',
          icon: ArrowsRightLeftIcon,
          eventId: event.id,
        },
        {
          id: 'resolve-follow-up',
          label: 'Resolve',
          children: FOLLOW_UP_RESOLUTION_OPTIONS.map(option => ({
            ...option,
            type: 'resolve-follow-up',
            eventId: event.id,
          })),
        },
        ...secondaryActions,
      ]
    : secondaryActions;
  const overflowActions = [
    ...(onQuickViewProfile && event.studentId
      ? [{
          id: 'quick-view-profile',
          type: 'quick-view-profile',
          label: 'Quick view profile',
          description: `Open ${event.studentName || 'this learner'}'s learner record without leaving Queue`,
          icon: UserCircleIcon,
        }]
      : []),
    ...operationalOverflowActions,
  ];

  const runMenuAction = action => {
    if (action.type === 'quick-view-profile') {
      onQuickViewProfile?.(event);
      return;
    }
    const actionToRun = action.type === 'void-record'
      ? {
          ...action,
          linkedRecords: getLinkedCorrectionRecords(event, eventsWithCounts),
        }
      : action;
    if (onAction) {
      onAction(actionToRun);
    } else {
      actionToRun.handler?.();
    }
  };

  // Determine alignment based on source and filter state
  // When viewing "All": Chat-style alignment (mentions left, consequences right)
  // When filtered: Full-width cards (no alignment needed)
  const isUserAction = event.sourceCollection === 'mentions';
  const alignmentClass = fullWidth || isFiltered
    ? 'w-full min-w-0' // Full width when filtered
    : compact
      ? isUserAction
        ? 'mr-auto w-[calc(100%-2rem)] max-w-full min-w-0'
        : 'ml-auto w-[calc(100%-2rem)] max-w-full min-w-0'
    : isUserAction
      ? 'mr-auto max-w-full sm:max-w-[70%] min-w-0' // Mentions align left
      : 'ml-auto max-w-full sm:max-w-[70%] min-w-0'; // Consequences align right

  // Navigate to triggered consequence
  const handleBadgeClick = (e) => {
    e.stopPropagation();
    const triggeredId = event.triggered?.[0];
    if (triggeredId) {
      const element = document.getElementById(`event-${triggeredId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
        }, 2000);
      } else if (event.originalData?.triggeredDetails?.length > 0) {
        // The resulting consequence can live just beyond this exact 30-record
        // page. Its hydrated detail is still part of this card's causal proof.
        setIsExpanded(true);
      }
    }
  };

  // Learner-name navigation remains the established full-page route. The
  // contextual profile is an explicit administrator action in the overflow
  // menu, not an invisible replacement for this link.
  const handleStudentClick = (e) => {
    e.stopPropagation();
    if (event.studentId) {
      onNavigate(`/studentprofile/${event.studentId}#behaviour`);
    }
  };

  // Navigate to trigger record
  const handleTriggerClick = (triggerId) => {
    const element = document.getElementById(`event-${triggerId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
      }, 2000);
    }
  };

  // Get rule type configuration
  const getRuleConfig = () => {
    const ruleTypeMap = {
      'zero-tolerance': { label: 'Zero Tolerance Rule', icon: ExclamationCircleIcon },
      'mention': { label: 'Mention Rule', icon: ScaleIcon },
      'threshold': { label: 'Threshold Rule', icon: ScaleIcon },
      'escalation': { label: 'Escalation Rule', icon: ArrowsRightLeftIcon },
      'accountability': { label: 'Accountability Rule', icon: CalendarDaysIcon },
      'contract': { label: 'Contract Rule', icon: DocumentTextIcon }
    };

    // Don't default to 'threshold' - show generic 'Rule' if type is missing
    const ruleType = event.originalData?.ruleType;
    return ruleTypeMap[ruleType] || { label: 'Rule', icon: ScaleIcon };
  };

  // Render message status indicator
  const renderMessageStatus = () => {
    if (event.sourceCollection !== 'mentions') return null;

    // Reactions stream in after the queue renders. While statuses are still loading,
    // show a minimal placeholder in the reaction slot only — it never covers or blocks
    // the card. Once loaded, a mention with no parent message shows nothing (as before).
    if (!event.messageStatus) {
      if (!messageStatusesLoaded) {
        return <div className="h-3.5 w-6 rounded bg-base-300 animate-pulse" aria-hidden="true" />;
      }
      return null;
    }

    const { status, response, respondedAt, createdAt } = event.messageStatus;

    if (status === 'responded' && response) {
      return (
        <div className="flex items-center gap-1.5 text-success">
          <div className="flex items-center -space-x-1">
            <CheckIcon className="w-3.5 h-3.5" />
            <CheckIcon className="w-3.5 h-3.5" />
          </div>
          {showMessageTimestamp && respondedAt && (
            <span className="text-xs opacity-70">
              {formatTimeAgo(respondedAt)}
            </span>
          )}
        </div>
      );
    }

    if (status === 'sent' || status === 'delivered') {
      return (
        <div className="flex items-center gap-1.5 text-blue-500">
          <CheckIcon className="w-3.5 h-3.5" />
          {showMessageTimestamp && createdAt && (
            <span className="text-xs">
              {formatTimeAgo(createdAt)}
            </span>
          )}
        </div>
      );
    }

    if (status === 'queued') {
      return <QueuedCountdown />;
    }

    return null;
  };

  const cardStyle = {
    maxWidth: '100%',
    contain: 'layout'
  };

  // Add border color using the dynamic color from Firestore
  if (!isUserAction && !isFiltered) {
    // Always add a border - use fallback colors if dynamic ones aren't available
    cardStyle.borderLeftWidth = '4px';
    cardStyle.borderLeftStyle = 'solid';

    if (typeConfig.borderHexColor || typeConfig.hexColor) {
      // Use dynamic color if available
      cardStyle.borderLeftColor = typeConfig.borderHexColor || typeConfig.hexColor;
    } else {
      // Fallback to hardcoded colors that match the badges
      const fallbackColors = {
        'demerit': '#F59E0B',
        'detention': '#F97316',
        'warning': '#EAB308',
        'suspension': '#EF4444',
        'friday-detention': '#6366F1',
        'saturday-detention': '#A855F7',
        'homework-detention': '#EC4899',
        'privilege-loss': '#64748B',
        'referral': '#10B981'
      };
      cardStyle.borderLeftColor = fallbackColors[event.type] || '#6B7280';
    }


  }

  // Keep the type tint on the theme's surface so dark-mode text stays legible.
  const bgColorClass = (!isUserAction && !isFiltered) ? (typeConfig.bgColor || '') : '';
  if (bgColorClass && cardStyle.borderLeftColor) {
    cardStyle.backgroundImage = `linear-gradient(to bottom right, color-mix(in oklab, ${cardStyle.borderLeftColor} 5%, var(--color-base-100)), var(--color-base-100))`;
  }

  // Pending adjudication — show skeleton treatment
  const isPendingAdjudication = event.adjudicationStatus === 'pending';

  if (isPendingAdjudication) {
    return (
      <motion.div
        id={anchorId || `event-${event.id}`}
        data-timeline-event-id={event.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => (selectionMode ? onToggleSelection(event.id) : onClick?.())}
        className={`
          card ${compact ? 'rounded-lg' : 'rounded-2xl'} bg-base-100 border-2 border-dashed border-warning/40
          ${alignmentClass}
          ${selectionMode || onClick ? 'cursor-pointer' : ''}
          ${isSelected ? 'border-primary border-2 bg-primary/5' : ''}
          ${highlighted ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
      >
        <div className="card-body p-0">
          {compact ? (
            <>
              <div className="p-2 min-w-0">
                <div className="flex items-start gap-2 min-w-0">
                  <Avatar
                    firstName={event.studentName?.split(' ')[0] || ''}
                    surname={event.studentName?.split(' ').slice(1).join(' ') || ''}
                    userId={event.studentId}
                    size="xs"
                    className="flex-shrink-0 mt-0.5"
                  />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      <button
                        className="font-semibold text-sm hover:text-primary hover:underline truncate min-w-0"
                        onClick={handleStudentClick}
                      >
                        {event.studentName}
                      </button>
                      <span className={`badge badge-xs ${typeConfig.iconBg} ${typeConfig.iconColor} flex-shrink-0`}>
                        {typeConfig.label}{event.sequentialCount ? ` ${event.sequentialCount}` : ''}
                      </span>
                    </div>
                    <div className="mt-1 flex min-w-0 items-center gap-1.5">
                      <span className="badge badge-xs badge-warning gap-1 flex-shrink-0">
                        <ShieldExclamationIcon className="w-3 h-3" />
                        Under Review
                      </span>
                      <span className="truncate text-xs text-base-content/60">
                        {consequenceSummary || 'Awaiting review'}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-base-content/50">
                      {formatTimestamp(event.timestamp)}
                    </div>
                  </div>
                  <RecordActionsMenu
                    actions={overflowActions}
                    studentName={event.studentName}
                    onSelect={runMenuAction}
                  />
                  <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-0.5 hover:bg-base-200 rounded transition-colors flex-shrink-0 mt-0.5"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-base-300/50 p-2 space-y-2">
                  <div className="text-sm text-base-content/80 break-words">
                    {consequenceSummary || 'Awaiting review'}
                  </div>
                  {contextSubjects.map(subject => (
                    <div key={subject} className="badge badge-xs badge-outline">{subject}</div>
                  ))}
                  {event.originalData?.triggeredByDetails?.length > 0 && (
                    <div className="space-y-1.5 min-w-0">
                      {[...event.originalData.triggeredByDetails].reverse().map(trigger => (
                        <TriggerRow
                          key={trigger.id}
                          trigger={trigger}
                          eventsWithCounts={eventsWithCounts}
                          eventTypeConfig={eventTypeConfig}
                          onNavigate={handleTriggerClick}
                          narrow
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
          <>
          {/* Header — avatar, name, timestamp, and adjudication badge */}
          <div className="p-2 border-b border-base-300/50 min-w-0">
            <div className="flex items-center justify-between min-w-0">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelection(event.id)}
                    className="checkbox checkbox-primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <Avatar
                  firstName={event.studentName?.split(' ')[0] || ''}
                  surname={event.studentName?.split(' ').slice(1).join(' ') || ''}
                  userId={event.studentId}
                  size={compact ? 'xs' : 'small'}
                  className="flex-shrink-0"
                />
                <div className="min-w-0 flex-1 overflow-hidden">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <button
                      className="font-semibold text-base hover:text-primary hover:underline truncate min-w-0"
                      onClick={handleStudentClick}
                    >
                      {event.studentName}
                    </button>
                    <span className={`badge badge-sm ${typeConfig.iconBg} ${typeConfig.iconColor} flex-shrink-0 max-w-full truncate`}>
                      {typeConfig.label}{event.sequentialCount ? ` ${event.sequentialCount}` : ''}
                    </span>
                    <span className="badge badge-sm badge-warning gap-1 flex-shrink-0">
                      <ShieldExclamationIcon className="w-3 h-3" />
                      Under Review
                    </span>
                  </div>
                  <div className="text-xs text-base-content/50 mt-0.5">
                    {formatTimestamp(event.timestamp)}
                  </div>
                </div>
                <RecordActionsMenu
                  actions={overflowActions}
                  studentName={event.studentName}
                  onSelect={runMenuAction}
                />
              </div>
            </div>
          </div>

          {/* Content — the reason this consequence was raised */}
          <div className="p-3 space-y-3">
            <div className="text-sm text-base-content/80">
              {consequenceSummary || 'Awaiting review'}
            </div>
            {contextSubjects.map(subject => (
              <div key={subject} className="badge badge-sm badge-outline">{subject}</div>
            ))}

            {/* Constituent offences — show the triggering records even while the
                item is under review, so the reviewer can see exactly what caused
                this consequence. Mirrors the expanded card's block below. */}
            {event.originalData?.triggeredByDetails?.length > 0 && (
              <div className="space-y-1.5 min-w-0">
                {[...event.originalData.triggeredByDetails].reverse().map(trigger => (
                  <TriggerRow
                    key={trigger.id}
                    trigger={trigger}
                    eventsWithCounts={eventsWithCounts}
                    eventTypeConfig={eventTypeConfig}
                    onNavigate={handleTriggerClick}
                    narrow={compact}
                  />
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
      id={anchorId || `event-${event.id}`}
      data-timeline-event-id={event.id}
      initial={false}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.15
      }}
      onClick={() => selectionMode && onToggleSelection(event.id)}
      style={cardStyle}
      className={`
        card ${compact ? 'rounded-lg' : 'rounded-2xl'} bg-base-100 border hover:shadow-lg transition-all
        ${alignmentClass}
        ${bgColorClass}
        ${selectionMode ? 'cursor-pointer' : ''}
        ${isSelected ? 'border-primary border-2 bg-primary/5' : 'border-base-300'}
        ${highlighted ? 'ring-2 ring-primary ring-offset-2' : ''}
      `}
    >
      <div className="card-body p-0">
        {/* Compact mode: simplified layout */}
        {compact ? (
          <div className="p-2">
            <div className="flex items-start gap-2">
              <Avatar
                firstName={event.studentName?.split(' ')[0] || ''}
                surname={event.studentName?.split(' ').slice(1).join(' ') || ''}
                userId={event.studentId}
                size="xs"
                className="flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 overflow-hidden">
                {/* Line 1: Name and Badge */}
                <div className="flex items-center gap-1.5 mb-0.5 overflow-hidden">
                  <button
                    className="font-semibold text-sm hover:text-primary hover:underline text-left"
                    onClick={handleStudentClick}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '150px'
                    }}
                  >
                    {event.studentName}
                  </button>
                  <span className={`badge badge-xs ${typeConfig.iconBg} ${typeConfig.iconColor} flex-shrink-0`}>
                    {typeConfig.label}
                  </span>
                </div>
                {/* Line 2: Offense and Time */}
                {!isExpanded && (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-base-content/70 overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                        {consequenceSummary}
                      </div>
                      <div className="text-xs text-base-content/50 whitespace-nowrap flex-shrink-0">
                        {formatTimestamp(event.timestamp)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
                      <div className="text-xs text-base-content/50">
                        {formatTimestamp(event.timestamp)}
                      </div>
                      {contextSubjects.map(subject => (
                        <div key={subject} className="badge badge-xs badge-outline">{subject}</div>
                      ))}
                      {renderMessageStatus()}
                    </div>
                  </>
                )}
              </div>
              <RecordActionsMenu
                actions={overflowActions}
                studentName={event.studentName}
                onSelect={runMenuAction}
              />
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-0.5 hover:bg-base-200 rounded transition-colors flex-shrink-0 mt-0.5"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
        ) : (
          /* Full mode: header strip */
          <div className="p-2 border-b border-base-300 min-w-0">
            <div className="flex flex-col gap-2 min-w-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Selection checkbox */}
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelection(event.id)}
                    className="checkbox checkbox-primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <Avatar
                  firstName={event.studentName?.split(' ')[0] || ''}
                  surname={event.studentName?.split(' ').slice(1).join(' ') || ''}
                  userId={event.studentId}
                  size="small"
                />
                <div className="min-w-0 flex-1 overflow-hidden">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <button
                      className="font-semibold text-base hover:text-primary hover:underline truncate min-w-0"
                      onClick={handleStudentClick}
                    >
                      {event.studentName}
                    </button>
                    {/* Badge - clickable if actioned */}
                    {(event.triggered?.length > 0 || event.originalData?.actioned) ? (
                      <button
                        onClick={handleBadgeClick}
                        className={`badge badge-sm ${typeConfig.iconBg} ${typeConfig.iconColor} flex items-center gap-1 hover:scale-105 transition-transform cursor-pointer flex-shrink-0 max-w-full truncate`}
                        title="View consequence this triggered"
                      >
                        {typeConfig.label} {event.sequentialCount}
                        <DocumentCheckIcon className="w-3 h-3" />
                      </button>
                    ) : (
                      <span className={`badge badge-sm ${typeConfig.iconBg} ${typeConfig.iconColor} flex-shrink-0 max-w-full truncate`}>
                        {typeConfig.label} {event.sequentialCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-xs text-base-content/70">
                      {formatTimestamp(event.timestamp)}
                    </div>
                    {contextSubjects.map(subject => (
                      <div key={subject} className="badge badge-xs badge-outline">{subject}</div>
                    ))}
                    {availabilityDisplayEnabled && event.originalData?.expectedSession?.start && (
                      <div className="text-xs text-base-content/60 flex items-center gap-1">
                        <CalendarDaysIcon className="w-3.5 h-3.5" />
                        {event.originalData.expectedSession.rescheduleMode === 'automatic-after-miss'
                          ? 'Automatically rescheduled · '
                          : ''}
                        Expected {formatTimestamp(event.originalData.expectedSession.start)}
                        {event.originalData.expectedSession.label ? ` · ${event.originalData.expectedSession.label}` : ''}
                        {event.originalData.expectedSession.location ? ` · ${event.originalData.expectedSession.location}` : ''}
                        {event.originalData.expectedSession.responsibleTarget && (
                          <span className="badge badge-ghost badge-xs">
                            Responsible: {
                              event.originalData.expectedSession
                                .responsibleTarget.label
                              || event.originalData.expectedSession
                                .responsibleTarget.name
                              || event.originalData.expectedSession
                                .responsibleTarget.id
                            }
                          </span>
                        )}
                      </div>
                    )}
                    {event.originalData?.followUpStatus === 'escalation-required' && (
                      <div className="badge badge-warning badge-outline badge-sm">
                        Reschedule limit reached · staff review
                      </div>
                    )}
                    {['communication-required', 'communication-failed'].includes(
                      event.originalData?.followUpStatus
                    ) && (
                      <div className="badge badge-error badge-outline badge-sm">
                        {event.originalData.followUpStatus === 'communication-failed'
                          ? 'Reschedule communication failed · staff follow-up'
                          : 'Reschedule communication required · staff follow-up'}
                      </div>
                    )}
                    {renderMessageStatus()}
                  </div>
                </div>
              </div>

              {(showRosterHeaderActions || overflowActions.length > 0) && (
                <div className="grid w-full grid-cols-2 gap-1.5 sm:w-auto sm:flex sm:flex-wrap sm:justify-end sm:flex-shrink-0">
                  {showRosterHeaderActions && headerActions.map(action => (
                    <StatusActionButton
                      key={action.id}
                      action={action}
                      isActive={activeActionId === action.id}
                      onClick={() => runHeaderAction(action)}
                    />
                  ))}
                  <RecordActionsMenu
                    actions={overflowActions}
                    studentName={event.studentName}
                    onSelect={runMenuAction}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content area - only show when expanded OR in full mode */}
        {(isExpanded || !compact) && (
        <div className={compact ? 'p-2 space-y-2' : 'p-3 space-y-2'}>
          {/* Offense - Always visible with expand toggle (full mode only) */}
          {!compact && (
          <div className="flex items-start gap-2 min-w-0">
            <div className="text-sm font-medium text-base-content min-w-0 flex-1 break-words">
              {consequenceSummary}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-base-200 rounded transition-colors"
                aria-label={isExpanded ? "Collapse details" : "Expand details"}
              >
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
          )}

          {/* Compact mode: show full offense when expanded */}
          {compact && (
            <div className="text-sm font-medium text-base-content break-words">
              {consequenceSummary}
            </div>
          )}

          {/* Obligation lifecycle and attendance are separate facts. */}
          {((showObligationState && obligationPresentation) || attendanceMarked || contextAction) && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {showObligationState && obligationPresentation && (
                    <div className={`badge gap-1 ${obligationPresentation.className}`}>
                      {obligationPresentation.label}
                    </div>
                  )}
                  {event.attendanceStatus === 'attended' && (
                    <div className="badge badge-success gap-1">
                      <CheckCircleIcon className="w-3 h-3" />
                      Attended
                    </div>
                  )}
                  {event.attendanceStatus === 'no-show' && (
                    <div className="badge badge-error gap-1">
                      <XCircleIcon className="w-3 h-3" />
                      Did Not Attend
                    </div>
                  )}
                  {event.attendanceStatus === 'partial' && (
                    <div className="badge badge-warning gap-1">
                      <ClockIcon className="w-3 h-3" />
                      Partial Attendance
                    </div>
                  )}
                  {event.attendanceStatus === 'late' && (
                    <div className="badge badge-info gap-1">
                      <ClockIcon className="w-3 h-3" />
                      Late
                    </div>
                  )}
                  {event.attendanceStatus === 'excused' && (
                    <div className="badge badge-warning gap-1">
                      <CheckCircleIcon className="w-3 h-3" />
                      {event.status === 'excused' ? 'Excused' : 'Excused for this session'}
                    </div>
                  )}
                  {event.attendanceStatus !== 'excused' &&
                    (event.attendanceStatus === 'absent' || event.status === 'absent') && (
                    <div className="badge badge-neutral gap-1">
                      <XCircleIcon className="w-3 h-3" />
                      Absent from school
                    </div>
                  )}
                  {event.servedDate && (
                    <span className="text-xs text-base-content/50">
                      {showCompletionLabel ? 'Completed ' : ''}{formatTimestamp(event.servedDate)}
                    </span>
                  )}
                  {event.status === 'excused' && (event.excusedAt || event.originalData?.excusedAt) && (
                    <span className="text-xs text-base-content/50">
                      Excused {formatTimestamp(event.excusedAt || event.originalData?.excusedAt)}
                    </span>
                  )}
                </div>
                {contextAction && (
                  <button
                    type="button"
                    className="btn btn-sm h-8 min-h-8 rounded-full border-base-300 bg-base-200 px-3 text-xs font-medium text-base-content/70 hover:bg-base-300"
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation();
                      setContextConfirmationOpen(true);
                    }}
                    aria-label={contextAction.ariaLabel}
                  >
                    {contextAction.label}
                  </button>
                )}
              </div>

            </div>
          )}

          {/* Expandable details section */}
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-3 overflow-hidden"
            >
              {/* Rule name for auto-generated consequences */}
              {!responseExplanation && event.ruleName && event.autoGenerated && event.originalData?.ruleType !== 'accountability-entry' && (() => {
                const ruleConfig = getRuleConfig();
                const RuleIcon = ruleConfig.icon;

                return (
                  <button
                    onClick={() => {
                      const ruleId = event.originalData?.triggeredByRule;
                      if (ruleId) {
                        onNavigate(`/discipline-rules#${ruleId}`);
                      } else {
                        onNavigate('/discipline-rules');
                      }
                    }}
                    className="flex items-start gap-1.5 text-xs text-primary hover:text-primary-focus transition-colors cursor-pointer min-w-0 w-full"
                  >
                    <RuleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span className="font-medium underline decoration-dotted hover:decoration-solid min-w-0 break-words text-left">
                      {ruleConfig.label}: {event.ruleName}
                    </span>
                  </button>
                );
              })()}

              {responseExplanation && (
                <section data-testid="response-explanation" className="space-y-2 text-xs text-base-content/80">
                  <h4 className="font-semibold text-base-content">Why this response</h4>
                  <p>{responseExplanation.phase}</p>
                  {responseExplanation.initialRule && (
                    <button type="button" className="text-left text-primary underline decoration-dotted"
                      onClick={() => onNavigate(`/discipline-rules${responseExplanation.initialRule.id ? `#${responseExplanation.initialRule.id}` : ''}`)}>
                      Initial rule: {responseExplanation.initialRule.name}
                    </button>
                  )}
                  {responseExplanation.steps.map(step => (
                    <div key={step.key} className="space-y-1 border-l-2 border-base-300 pl-3">
                      <p className="font-medium text-base-content">{step.transition}</p>
                      <button type="button" className="text-left text-primary underline decoration-dotted"
                        onClick={() => onNavigate(`/discipline-rules${step.ruleId ? `#${step.ruleId}` : ''}`)}>
                        {step.ruleName}
                      </button>
                      <p>{step.condition}</p>
                      {step.count && <p>{step.count}</p>}
                      {step.reset && <p>{step.reset}</p>}
                    </div>
                  ))}
                </section>
              )}

              {/* Category tag and level badge */}
              {((event.category || event.offenseCategory) || event.level) && (() => {
                const categoryName = event.category || event.offenseCategory;
                const fullCategory = categoryName ? categories.find(c => c.name === categoryName) || { name: categoryName } : null;
                return (
                  <div className="flex items-center gap-2 flex-wrap">
                    {event.level && (
                      <span className="badge badge-xs badge-outline">
                        lvl {event.level}
                      </span>
                    )}
                    {fullCategory && (
                      <CategoryTag
                        category={fullCategory}
                        size="small"
                        showIcon={true}
                      />
                    )}
                  </div>
                );
              })()}

              {/* Triggering records for auto-generated consequences.
                  Each row is drillable: a demerit expands to the mentions that
                  earned it, and so on down the chain. */}
              {event.originalData?.triggeredByDetails?.length > 0 && (
                <div className="space-y-1.5 min-w-0">
                  {[...event.originalData.triggeredByDetails].reverse().map(trigger => (
                    <TriggerRow
                      key={trigger.id}
                      trigger={trigger}
                      eventsWithCounts={eventsWithCounts}
                      eventTypeConfig={eventTypeConfig}
                      onNavigate={handleTriggerClick}
                      narrow={compact}
                    />
                  ))}
                </div>
              )}

              {event.originalData?.triggeredDetails?.length > 0 && (
                <div className="space-y-1.5 min-w-0">
                  <div className="text-xs font-medium text-base-content/60">Led to</div>
                  {event.originalData.triggeredDetails.map(result => (
                    <TriggerRow
                      key={result.id}
                      trigger={result}
                      eventsWithCounts={eventsWithCounts}
                      eventTypeConfig={eventTypeConfig}
                      onNavigate={handleTriggerClick}
                      narrow={compact}
                    />
                  ))}
                </div>
              )}

              {/* The consequence itself may be system-created. In that case,
                  identify the staff attached to its causal Mention records. */}
              {contextIssuedByNames.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-base-content/70">
                  <span className="font-medium">
                    {event.triggeredBy?.length > 0 ? 'Source issued by:' : 'Issued by:'}
                  </span>
                  <span>{contextIssuedByNames.join(', ')}</span>
                </div>
              )}

              {showAdministrationDetails && hasOpenFollowUp && isQueueConsequence &&
                requiresActionForEvent(event, eventTypeConfig) && (
                <ObligationAuditDetails event={event} actorNamesById={auditActorNamesById} />
              )}

              {/* Notes */}
              {event.notes && !event.autoGenerated && (
                <div className="px-2 py-1.5 bg-base-200/30 rounded-lg min-w-0">
                  <p className="text-xs text-base-content/70 italic break-words">
                    &ldquo;{event.notes}&rdquo;
                  </p>
                </div>
              )}

              {/* Attachments (additive — any domain event may carry images/documents) */}
              {event.attachments?.length > 0 && (
                <div className="px-2 py-1.5 min-w-0">
                  <AttachmentList
                    attachments={event.attachments}
                    readOnly
                    sourceRef={{
                      sourceType: 'eventAttachment',
                      collection: event.sourceCollection || (event.type === 'mention' ? 'mentions' : 'disciplineQueue'),
                      sourceId: event.id,
                      recordId: event.id,
                      studentId: event.studentId,
                    }}
                  />
                </div>
              )}

            </motion.div>
          )}
        </div>
        )}
      </div>
      </motion.div>
      <Modal
        isOpen={contextConfirmationOpen}
        onClose={() => setContextConfirmationOpen(false)}
        title={contextAction?.confirmTitle || 'Open outstanding obligations?'}
        size="sm"
        actions={(
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setContextConfirmationOpen(false)}
              autoFocus
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setContextConfirmationOpen(false);
                contextAction?.onClick();
              }}
            >
              Open obligations
            </button>
          </>
        )}
      >
        <p className="text-sm text-base-content/70">
          {contextAction?.confirmMessage || 'This will leave Follow-up and open the learner’s Outstanding Obligations section.'}
        </p>
      </Modal>
    </>
  );
};

export default memo(TimelineEventCard, (prev, next) => {
  const sameActionIds = prev.availableActions?.length === next.availableActions?.length
    && prev.availableActions?.every((action, index) => action.id === next.availableActions[index]?.id);

  return prev.event === next.event
    && prev.isSelected === next.isSelected
    && prev.selectionMode === next.selectionMode
    && prev.isFiltered === next.isFiltered
    && prev.compact === next.compact
    && prev.fullWidth === next.fullWidth
    && prev.showActionControls === next.showActionControls
    && prev.showFollowUpControls === next.showFollowUpControls
    && prev.showMessageTimestamp === next.showMessageTimestamp
    && prev.messageStatusesLoaded === next.messageStatusesLoaded
    && prev.initiallyExpanded === next.initiallyExpanded
    && prev.highlighted === next.highlighted
    && prev.anchorId === next.anchorId
    && prev.canManageRoster === next.canManageRoster
    && prev.contextAction === next.contextAction
    && prev.showObligationState === next.showObligationState
    && prev.showCompletionLabel === next.showCompletionLabel
    && prev.showAdministrationDetails === next.showAdministrationDetails
    && prev.isActionedInCurrentSession === next.isActionedInCurrentSession
    && prev.onQuickViewProfile === next.onQuickViewProfile
    && sameActionIds;
});
